import axios from 'axios';
import type { Teacher, Class, Line, TimetableSlot, TimetableSlotCreate, ClassAllocation, ClashCheck, TimetableVersion, LinesWithTripletsResponse, IndependentClashScanResult, AutoPackInfo, AutoPackScanResult, AutoPackApplyResult, ConstraintsAnalysisResult } from '../types';

// Use environment variable for API URL, fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Get credentials from sessionStorage
const getAuthCredentials = () => {
  const username = sessionStorage.getItem('auth_username');
  const password = sessionStorage.getItem('auth_password');
  return username && password ? { username, password } : undefined;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 second timeout (for auto-pack algorithm)
});

// Add auth credentials to each request
api.interceptors.request.use((config) => {
  const auth = getAuthCredentials();
  if (auth) {
    config.auth = auth;
  }
  return config;
});

// Teacher API
export const teachersApi = {
  getAll: (): Promise<{ data: Teacher[] }> => api.get('/teachers/'),
  create: (teacher: Teacher) => api.post('/teachers/', teacher),
  update: (code: string, teacher: Teacher) => api.put(`/teachers/${code}`, teacher),
  delete: (code: string) => api.delete(`/teachers/${code}`),
  uploadExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/teachers/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Classes API
export const classesApi = {
  getAll: (): Promise<{ data: Class[] }> => api.get('/classes/'),
  getMissingTeachers: (): Promise<{ data: Array<{ class_code: string; teacher_code: string; teacher_field: string }> }> => 
    api.get('/classes/missing-teachers'),
  create: (classData: Class) => api.post('/classes/', classData),
  update: (code: string, classData: Class) => api.put(`/classes/${code}`, classData),
  delete: (code: string) => api.delete(`/classes/${code}`),
  uploadExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/classes/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Lines API
export const linesApi = {
  getAll: (): Promise<{ data: LinesWithTripletsResponse }> => api.get('/lines/'),
  create: (line: Line) => api.post('/lines/', line),
  update: (code: string, line: Line) => api.put(`/lines/${code}`, line),
  delete: (code: string) => api.delete(`/lines/${code}`),
  uploadExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/lines/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Learners API
export const learnersApi = {
  getAll: (params?: { curriculum_type?: string; grade?: number; upload_section?: string }) => 
    api.get('/learners/', { params }),
  getById: (id: number) => api.get(`/learners/${id}`),
  create: (learner: any) => api.post('/learners/', learner),
  update: (id: number, learner: any) => api.put(`/learners/${id}`, learner),
  delete: (id: number) => api.delete(`/learners/${id}`),
  deleteAll: () => api.delete('/learners/all/'),
  getStats: () => api.get('/learners/stats/'),
  uploadCaps8to12: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/learners/caps-8-12/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadCam8to11: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/learners/cam-8-11/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Timetable API
export const timetableApi = {
  getCurrentTimetable: (): Promise<{ data: TimetableSlot[] }> => api.get('/timetable/'),
  createSlot: (slotData: TimetableSlotCreate): Promise<{ data: TimetableSlot }> => api.post('/timetable/slot/', slotData),
  updateSlot: (slotId: number, teacherCode?: string, room?: string): Promise<{ data: { success: boolean; message: string; slot: TimetableSlot } }> => {
    const params: any = {};
    if (teacherCode !== undefined) params.teacher_code = teacherCode;
    if (room !== undefined) params.room = room;
    return api.put(`/timetable/slot/${slotId}`, null, { params });
  },
  getPartnerClasses: (): Promise<{ data: { partners: { [key: string]: string } } }> => api.get('/timetable/partner-classes'),
  swapPartnerClass: (slotId: number): Promise<{ data: { success: boolean; message: string; old_code: string; new_code: string; slot: TimetableSlot } }> => api.post(`/timetable/slot/${slotId}/swap-partner`),
  deleteSlot: (slotId: number): Promise<{ data: { success: boolean; message: string } }> => api.delete(`/timetable/slot/${slotId}`),
  deleteLineGroup: (day: number, period: number, classCode: string): Promise<{ data: { success: boolean; deleted_count: number; line_code: string } }> => api.delete(`/timetable/delete-line-group/${day}/${period}/${classCode}`),
  clearTimetable: (): Promise<{ data: { success: boolean; deleted_count: number; message: string } }> => api.delete('/timetable/clear/'),
  getGridSlots: (day: number, period: number): Promise<{ data: TimetableSlot[] }> => api.get(`/timetable/grid/${day}/${period}`),
  getClassAllocation: (classCode: string): Promise<{ data: ClassAllocation }> => api.get(`/timetable/allocation/${classCode}`),
  getBatchClassAllocations: (classCodes: string[]): Promise<{ data: { [classCode: string]: ClassAllocation } }> => api.post('/timetable/allocations/batch', classCodes),
  checkClash: (day: number, period: number, classCode: string): Promise<{ data: ClashCheck }> => api.get(`/timetable/check-clash/${day}/${period}/${classCode}`),
  checkClashUnified: (clashData: { day: number; period: number; code: string; class_code: string; teacher1: string; teacher2?: string; room?: string; type?: string }): Promise<{ data: ClashCheck }> => api.post('/timetable/check-clash/', clashData),
  debugTimetable: (): Promise<{ data: { total_slots: number; slots: any[] } }> => api.get('/timetable/debug/'),
  independentClashScan: (): Promise<{ data: IndependentClashScanResult }> => api.get('/timetable/independent_clash_scan'),
  getAutoPackInfo: (grades: number[]): Promise<{ data: AutoPackInfo }> => api.get('/timetable/autopack-info', { params: { grades: grades.join(',') } }),
  autoPackScan: (grades: number[], maxExplorations: number = 10000): Promise<{ data: AutoPackScanResult }> => 
    api.post('/timetable/autopack-scan', { grades, max_explorations: maxExplorations }, { 
      timeout: 900000  // 15 minute timeout (backend max is 12 minutes)
    }),
  applyAutoPackSolution: (grades: number[], maxExplorations: number = 10000, allowPartial: boolean = false): Promise<{ data: AutoPackApplyResult }> => 
    api.post('/timetable/autopack-apply', { grades, max_explorations: maxExplorations, allow_partial: allowPartial }, { 
      timeout: 900000  // 15 minute timeout (backend max is 12 minutes)
    }),
  getConstraintsAnalysis: (grades: number[]): Promise<{ data: ConstraintsAnalysisResult }> => 
    api.get('/timetable/constraints-analysis', { params: { grades: grades.join(',') } }),
};

// Settings API
export const settingsApi = {
  get: () => api.get('/settings/'),
  update: (settings: any) => api.post('/settings/', settings),
};

// Progress API
export const progressApi = {
  get: () => api.get('/progress/'),
};

// Version Control API
export const versionsApi = {
  getAll: (): Promise<{ data: TimetableVersion[] }> => api.get('/versions/'),
  save: (version: { name: string; description?: string }) => api.post('/versions/save/', version),
  load: (versionId: number) => api.post(`/versions/load/${versionId}`),
  delete: (versionId: number) => api.delete(`/versions/${versionId}`),
};

// Teacher Blocked Lessons API
export const blockedLessonsApi = {
  getAll: (teacherCode?: string) => api.get('/teacher-blocked-lessons/', { 
    params: { 
      ...(teacherCode ? { teacher_code: teacherCode } : {}),
      _t: Date.now() // Cache busting timestamp
    }
  }),
  create: (blockedLesson: any) => api.post('/teacher-blocked-lessons/', blockedLesson),
  delete: (blockedLessonId: number) => api.delete(`/teacher-blocked-lessons/${blockedLessonId}`),
  deleteAll: (teacherCode: string) => api.delete(`/teacher-blocked-lessons/teacher/${teacherCode}`),
  bulkUpdate: (teacherCode: string, blockedPeriods: any[]) => api.post('/teacher-blocked-lessons/bulk-update/', blockedPeriods, { params: { teacher_code: teacherCode } }),
};

// Export API
export const exportApi = {
  exportTimetableExcel: () => {
    return api.get('/export/timetable-excel/', { 
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
  },
  getTimetableStats: () => api.get('/timetable/stats/'),
  generateLearnerSchedule: (learnerId: string) => api.post(`/generate-learner-schedule/${learnerId}`)
};

export default api;